import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { toast } from 'sonner';
import { PageHeader } from '../components/common/PageHeader';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { QRCodeCanvas } from 'qrcode.react';
import { TrackMap } from '../components/maps/OsmMap';
import { DeliverySettings } from '../components/settings/DeliverySettings';
import { fmtEur } from '../lib/constants';
import { useRealtime } from '../lib/realtime';
import {
  Bike, ShoppingBag, Phone, Banknote, CreditCard, Check, X, ChefHat,
  Package, MapPin, Clock, User, RadioTower, Settings, QrCode, Copy, Download,
} from 'lucide-react';

const COLUMNS = ['pending', 'accepted', 'preparing', 'ready', 'in_transit'];
const STATUS_TOKEN = {
  pending: 'bg-kds-new', accepted: 'bg-kds-preparing', preparing: 'bg-kds-preparing',
  ready: 'bg-primary', in_transit: 'bg-primary', delivered: 'bg-kds-delivered',
};

const OrderCard = ({ o, t, onOpen, onAction }) => {
  const Icon = o.type === 'asporto' ? ShoppingBag : Bike;
  return (
    <Card className="relative overflow-hidden p-3 shadow-sm-token hover:shadow-md-token transition-shadow cursor-pointer" data-testid="staff-order-card" onClick={() => onOpen(o)}>
      <span className={`absolute left-0 top-0 h-full w-1.5 ${STATUS_TOKEN[o.status] || 'bg-muted'}`} />
      <div className="pl-2">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 font-display font-bold text-sm">
            <Icon className="h-4 w-4 text-primary" /> {(o.customer || {}).name || '—'}
          </span>
          <Badge variant="outline" className="tabular text-[11px]">{o.time_slot === 'asap' ? t('offsite.asap') : o.time_slot}</Badge>
        </div>
        <div className="mt-1 text-xs text-muted-foreground flex items-center gap-2">
          <Phone className="h-3 w-3" /> {(o.customer || {}).phone || '—'}
        </div>
        <div className="mt-1.5 flex items-center gap-2 text-xs">
          <Badge variant="secondary" className="gap-1">{(o.payment || {}).method === 'card' ? <CreditCard className="h-3 w-3" /> : <Banknote className="h-3 w-3" />}{t(`offsite.${(o.payment || {}).method === 'card' ? 'card' : 'cash'}`)}</Badge>
          <span className="text-muted-foreground">{(o.items || []).reduce((n, it) => n + (it.qty || 0), 0)} {t('offsite.board.items')}</span>
          <span className="ml-auto font-display font-black text-primary tabular">{fmtEur(o.total)}</span>
        </div>
        {o.courier_name && <div className="mt-1.5 text-xs text-primary flex items-center gap-1"><Bike className="h-3 w-3" /> {o.courier_name}</div>}
        <div className="mt-2.5 flex gap-1.5" onClick={(e) => e.stopPropagation()}>
          {o.status === 'pending' && (
            <>
              <Button size="sm" className="h-8 flex-1 rounded-lg" data-testid="staff-order-status-action-button" onClick={() => onAction(o, 'accepted')}><Check className="h-3.5 w-3.5 mr-1" />{t('offsite.board.accept')}</Button>
              <Button size="sm" variant="outline" className="h-8 rounded-lg text-destructive" onClick={() => onAction(o, 'reject')}><X className="h-3.5 w-3.5" /></Button>
            </>
          )}
          {o.status === 'accepted' && <Button size="sm" className="h-8 flex-1 rounded-lg" data-testid="staff-order-status-action-button" onClick={() => onAction(o, 'preparing')}><ChefHat className="h-3.5 w-3.5 mr-1" />{t('offsite.board.prepare')}</Button>}
          {o.status === 'preparing' && <Button size="sm" className="h-8 flex-1 rounded-lg" data-testid="staff-order-status-action-button" onClick={() => onAction(o, 'ready')}><Package className="h-3.5 w-3.5 mr-1" />{t('offsite.board.ready')}</Button>}
          {o.status === 'ready' && o.type === 'asporto' && <Button size="sm" className="h-8 flex-1 rounded-lg" onClick={() => onAction(o, 'delivered')}><Check className="h-3.5 w-3.5 mr-1" />{t('offsite.board.markDelivered')}</Button>}
          {o.status === 'ready' && o.type === 'domicilio' && <Button size="sm" className="h-8 flex-1 rounded-lg" onClick={() => onOpen(o)}><Bike className="h-3.5 w-3.5 mr-1" />{t('offsite.board.assign')}</Button>}
          {o.status === 'in_transit' && <Button size="sm" variant="outline" className="h-8 flex-1 rounded-lg" onClick={() => onOpen(o)}>{t('offsite.board.details')}</Button>}
        </div>
      </div>
    </Card>
  );
};

export default function AsportoDomicilioPage() {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [assignTo, setAssignTo] = useState('');
  const [restGeo, setRestGeo] = useState(null);
  const [restaurantId, setRestaurantId] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/offsite/orders', { params: { scope: 'active' } });
      setOrders(data);
      if (selected) { const fresh = data.find((o) => o.id === selected.id); if (fresh) setSelected(fresh); }
    } catch (e) { /* */ }
  }, [selected]);

  useEffect(() => { load(); const tm = setInterval(load, 8000); return () => clearInterval(tm); }, [load]);
  useEffect(() => { api.get('/offsite/couriers').then(({ data }) => setCouriers(data)).catch(() => {}); }, []);
  useEffect(() => { api.get('/offsite/config').then(({ data }) => { setRestGeo(data.restaurant_geo); setRestaurantId(data.restaurant_id); }).catch(() => {}); }, []);
  useRealtime((msg) => { if (msg && msg.event === 'offsite_orders') load(); });

  const action = async (o, status) => {
    try {
      if (status === 'reject') await api.post(`/offsite/orders/${o.id}/reject`);
      else await api.post(`/offsite/orders/${o.id}/status`, { status });
      toast.success(t(`offsite.status.${status === 'reject' ? 'rejected' : status}`));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const assign = async () => {
    if (!assignTo || !selected) return;
    try {
      await api.post(`/offsite/orders/${selected.id}/assign`, { courier_id: assignTo });
      toast.success(t('offsite.board.assign'));
      setAssignTo(''); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const filtered = orders.filter((o) => filter === 'all' || o.type === (filter === 'takeaway' ? 'asporto' : 'domicilio'));
  const byStatus = (st) => filtered.filter((o) => o.status === st);

  return (
    <div className="space-y-4" data-testid="staff-delivery-board">
      <PageHeader title={t('offsite.board.title')} subtitle={t('offsite.settings.subtitle')}>
        <Badge variant="outline" className="gap-1.5 text-pos-free border-pos-free/40"><RadioTower className="h-3.5 w-3.5" /> Live</Badge>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setQrOpen(true)} data-testid="delivery-qr-open-button">
          <QrCode className="h-4 w-4" /> {t('offsite.qr.title')}
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setSettingsOpen(true)} data-testid="delivery-settings-open-button">
          <Settings className="h-4 w-4" /> {t('offsite.settings.title')}
        </Button>
      </PageHeader>

      {/* QR del menu d'ordine Asporto/Domicilio (separato dalla Carta Digitale) */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-sm" data-testid="delivery-qr-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><QrCode className="h-5 w-5 text-primary" /> {t('offsite.qr.title')}</DialogTitle>
          </DialogHeader>
          {(() => {
            const orderUrl = restaurantId ? `${window.location.origin}/ordina/${restaurantId}` : '';
            const download = () => {
              const canvas = document.getElementById('offsite-order-qr');
              if (!canvas) return;
              const a = document.createElement('a');
              a.href = canvas.toDataURL('image/png');
              a.download = 'qr-asporto-domicilio.png';
              a.click();
            };
            return (
              <div className="flex flex-col items-center gap-3">
                <p className="text-sm text-muted-foreground text-center">{t('offsite.qr.hint')}</p>
                <div className="rounded-2xl border bg-white p-4">
                  {orderUrl ? <QRCodeCanvas id="offsite-order-qr" value={orderUrl} size={220} level="H" includeMargin={false} /> : null}
                </div>
                <div className="w-full flex items-center gap-2 rounded-xl border bg-muted/40 px-3 py-2">
                  <span className="text-xs truncate flex-1" data-testid="delivery-qr-url">{orderUrl}</span>
                  <button onClick={() => { try { navigator.clipboard.writeText(orderUrl); toast.success(t('offsite.codeCopied')); } catch (e) {} }} className="text-primary shrink-0" data-testid="delivery-qr-copy"><Copy className="h-4 w-4" /></button>
                </div>
                <Button onClick={download} className="w-full rounded-xl" data-testid="delivery-qr-download"><Download className="h-4 w-4 mr-1.5" /> {t('offsite.qr.download')}</Button>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Configurazione Asporto e Domicilio (dentro la funzione, non in Impostazioni) */}
      <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-auto" data-testid="delivery-settings-sheet">
          <SheetHeader className="mb-3">
            <SheetTitle className="font-display flex items-center gap-2"><Settings className="h-5 w-5 text-primary" /> {t('offsite.settings.title')}</SheetTitle>
          </SheetHeader>
          <DeliverySettings onSaved={(cfg) => { setRestGeo(cfg?.restaurant_geo); }} />
        </SheetContent>
      </Sheet>

      <Tabs value={filter} onValueChange={setFilter} data-testid="staff-delivery-filter-tabs">
        <TabsList>
          <TabsTrigger value="all">{t('offsite.board.all')}</TabsTrigger>
          <TabsTrigger value="takeaway">{t('offsite.board.takeaway')}</TabsTrigger>
          <TabsTrigger value="delivery">{t('offsite.board.delivery')}</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex gap-4 overflow-x-auto pb-3">
        {COLUMNS.map((col) => {
          const items = byStatus(col);
          return (
            <div key={col} className="min-w-[300px] w-[300px] shrink-0">
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="font-display font-bold text-sm">{t(`offsite.status.${col}`)}</span>
                <Badge variant="secondary" className="tabular">{items.length}</Badge>
              </div>
              <ScrollArea className="h-[calc(100vh-260px)]">
                <div className="space-y-2.5 pr-2">
                  {items.length === 0 ? (
                    <div className="text-xs text-muted-foreground text-center py-8">{t('offsite.board.empty')}</div>
                  ) : items.map((o) => <OrderCard key={o.id} o={o} t={t} onOpen={(x) => { setSelected(x); setAssignTo(''); }} onAction={action} />)}
                </div>
              </ScrollArea>
            </div>
          );
        })}
      </div>

      {/* Details */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-auto" data-testid="staff-order-details-sheet">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display flex items-center gap-2">
                  {selected.type === 'asporto' ? <ShoppingBag className="h-5 w-5 text-primary" /> : <Bike className="h-5 w-5 text-primary" />}
                  {t(`offsite.type.${selected.type}`)} · {t(`offsite.status.${selected.status}`)}
                </SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-4 text-sm">
                <div className="rounded-xl border bg-card p-3 space-y-1.5">
                  <div className="flex items-center gap-2 font-semibold"><User className="h-4 w-4 text-muted-foreground" />{(selected.customer || {}).name}</div>
                  <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /><a href={`tel:${(selected.customer || {}).phone}`} className="text-primary">{(selected.customer || {}).phone}</a></div>
                  {selected.address && <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />{selected.address}</div>}
                  {selected.notes && <div className="text-muted-foreground italic">“{selected.notes}”</div>}
                  {selected.time_slot && <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-muted-foreground" />{selected.time_slot === 'asap' ? t('offsite.asap') : selected.time_slot}</div>}
                </div>

                <div className="rounded-xl border bg-card p-3">
                  {(selected.items || []).map((it, i) => (
                    <div key={i} className="flex justify-between py-0.5">
                      <span>{it.qty}× {it.name}{it.variant ? ` · ${it.variant}` : ''}</span>
                      <span className="tabular text-muted-foreground">{fmtEur((it.unit_price + (it.extras || []).reduce((s, e) => s + e.price, 0)) * it.qty)}</span>
                    </div>
                  ))}
                  <Separator className="my-2" />
                  {selected.delivery_fee > 0 && <div className="flex justify-between text-muted-foreground"><span>{t('offsite.deliveryFee')}</span><span className="tabular">{fmtEur(selected.delivery_fee)}</span></div>}
                  <div className="flex justify-between font-display font-bold"><span>{t('publicMenu.order.total')}</span><span className="tabular text-primary">{fmtEur(selected.total)}</span></div>
                  <div className="mt-2 flex items-center gap-2 text-xs">
                    <Badge variant="secondary" className="gap-1">{(selected.payment || {}).method === 'card' ? <CreditCard className="h-3 w-3" /> : <Banknote className="h-3 w-3" />}{t(`offsite.${(selected.payment || {}).method === 'card' ? 'card' : 'cash'}`)}</Badge>
                    {(selected.payment || {}).cash_change != null && <span className="text-muted-foreground">{t('offsite.board.change')} {fmtEur(selected.payment.cash_change)}</span>}
                  </div>
                </div>

                {/* Confirmation code visible to staff */}
                <div className="rounded-xl border bg-secondary p-3 text-center">
                  <div className="text-xs text-muted-foreground">{t('offsite.board.code')}</div>
                  <div className="font-mono tabular tracking-widest text-2xl font-bold">{selected.confirmation_code}</div>
                </div>

                {/* Domicilio: courier assign + live map */}
                {selected.type === 'domicilio' && (
                  <>
                    {!selected.courier_id && selected.status === 'ready' && (
                      <div className="flex gap-2">
                        <Select value={assignTo} onValueChange={setAssignTo}>
                          <SelectTrigger data-testid="staff-assign-courier-select" className="flex-1"><SelectValue placeholder={t('offsite.board.assign')} /></SelectTrigger>
                          <SelectContent>
                            {couriers.length === 0 ? <div className="px-3 py-2 text-sm text-muted-foreground">{t('offsite.board.noCourier')}</div>
                              : couriers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button onClick={assign} disabled={!assignTo} data-testid="staff-assign-courier-button">{t('offsite.board.assign')}</Button>
                      </div>
                    )}
                    {selected.geo && (
                      <TrackMap restaurant={restGeo} customer={selected.geo} courier={selected.courier_geo}
                        className="h-[220px] rounded-xl overflow-hidden border" />
                    )}
                    {selected.courier_geo && <p className="text-xs text-pos-free flex items-center gap-1.5"><Bike className="h-3.5 w-3.5" /> {t('offsite.courierLive')}</p>}
                  </>
                )}

                {/* status actions */}
                <div className="flex flex-wrap gap-2">
                  {selected.status === 'pending' && <>
                    <Button className="flex-1" onClick={() => action(selected, 'accepted')}>{t('offsite.board.accept')}</Button>
                    <Button variant="outline" className="text-destructive" onClick={() => action(selected, 'reject')}>{t('offsite.board.reject')}</Button>
                  </>}
                  {selected.status === 'accepted' && <Button className="flex-1" onClick={() => action(selected, 'preparing')}>{t('offsite.board.prepare')}</Button>}
                  {selected.status === 'preparing' && <Button className="flex-1" onClick={() => action(selected, 'ready')}>{t('offsite.board.ready')}</Button>}
                  {(selected.status === 'ready' || selected.status === 'in_transit') && <Button className="flex-1" data-testid="staff-mark-delivered-button" onClick={() => action(selected, 'delivered')}>{t('offsite.board.markDelivered')}</Button>}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
